import { Component, Input, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin } from "rxjs";
import { finalize } from "rxjs/operators";
import { AlertService } from "@modules/alert";
import {
  EntitySetting,
  ScopeGroup,
} from "src/app/api/entity-settings/models/entity-setting.model";
import { EntitySettingsService } from "src/app/api/entity-settings/services/entity-settings.service";

const MORPH_MODEL = "entity";
const GOOGLE_SCOPE_PREFIX = "https://www.googleapis.com/auth/";

interface DriverDefinition {
  driver: string;
  settingName: string;
  titleKey: string;
  subtitleKey: string;
}

// Microsoft first, then Google (per design).
const DRIVERS: DriverDefinition[] = [
  {
    driver: "microsoft",
    settingName: "oauth_scopes_microsoft",
    titleKey: "COMPANY.ACCOUNT-SCOPES.MICROSOFT",
    subtitleKey: "COMPANY.ACCOUNT-SCOPES.MICROSOFT-SUBTITLE",
  },
  {
    driver: "google",
    settingName: "oauth_scopes_google",
    titleKey: "COMPANY.ACCOUNT-SCOPES.GOOGLE",
    subtitleKey: "COMPANY.ACCOUNT-SCOPES.GOOGLE-SUBTITLE",
  },
];

interface ScopeRowView {
  label: string;
  value: string;
  display: string;
}

interface ScopeGroupView {
  name: string;
  rows: ScopeRowView[];
}

interface LockedScopeView {
  value: string;
  display: string;
}

interface DriverView extends DriverDefinition {
  isMicrosoft: boolean;
  isGoogle: boolean;
  expanded: boolean;
  saving: boolean;
  groups: ScopeGroupView[];
  locked: LockedScopeView[];
  selected: Set<string>;
  initial: Set<string>;
}

@Component({
  selector: "app-account-scopes",
  templateUrl: "./account-scopes.component.html",
  styleUrls: ["./account-scopes.component.scss"],
})
export class AccountScopesComponent implements OnInit {
  @Input() entityType!: string;
  @Input() entityId!: number;

  loading = false;
  loadFailed = false;
  drivers: DriverView[] = [];

  constructor(
    private settings: EntitySettingsService,
    private alert: AlertService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loading = true;

    forkJoin(
      DRIVERS.map((definition) =>
        this.settings.getSetting(
          MORPH_MODEL,
          this.entityId,
          definition.settingName
        )
      )
    )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (settings) => {
          this.drivers = settings.map((setting, index) =>
            this.toDriverView(DRIVERS[index], setting)
          );
        },
        error: () => {
          this.loadFailed = true;
        },
      });
  }

  isSelected(driver: DriverView, value: string): boolean {
    return driver.selected.has(value);
  }

  toggle(driver: DriverView, value: string): void {
    if (driver.selected.has(value)) {
      driver.selected.delete(value);
    } else {
      driver.selected.add(value);
    }
  }

  isGroupAllSelected(driver: DriverView, group: ScopeGroupView): boolean {
    return group.rows.every((row) => driver.selected.has(row.value));
  }

  toggleExpand(driver: DriverView): void {
    driver.expanded = !driver.expanded;
  }

  toggleGroup(driver: DriverView, group: ScopeGroupView): void {
    const selectAll = !this.isGroupAllSelected(driver, group);

    group.rows.forEach((row) => {
      if (selectAll) {
        driver.selected.add(row.value);
      } else {
        driver.selected.delete(row.value);
      }
    });
  }

  isDirty(driver: DriverView): boolean {
    if (driver.selected.size !== driver.initial.size) {
      return true;
    }

    for (const value of driver.selected) {
      if (!driver.initial.has(value)) {
        return true;
      }
    }

    return false;
  }

  saveDriver(driver: DriverView): void {
    if (!this.isDirty(driver) || driver.saving) {
      return;
    }

    driver.saving = true;

    this.settings
      .updateSetting(
        MORPH_MODEL,
        this.entityId,
        driver.settingName,
        Array.from(driver.selected)
      )
      .pipe(finalize(() => (driver.saving = false)))
      .subscribe({
        next: (setting) => {
          driver.selected = new Set(this.normaliseValue(setting.value));
          driver.initial = new Set(driver.selected);
          this.alert.success(
            this.translate.instant("COMPANY.ACCOUNT-SCOPES.SAVED")
          );
        },
        error: () => {
          this.alert.error(
            this.translate.instant("COMPANY.ACCOUNT-SCOPES.SAVE-ERROR")
          );
        },
      });
  }

  shortenScope(value: string): string {
    return value.startsWith(GOOGLE_SCOPE_PREFIX)
      ? value.slice(GOOGLE_SCOPE_PREFIX.length)
      : value;
  }

  private toDriverView(
    definition: DriverDefinition,
    setting: EntitySetting
  ): DriverView {
    const sourceGroups: ScopeGroup[] = setting.annotations?.groups ?? [];
    const locked = setting.annotations?.locked ?? [];

    const groups: ScopeGroupView[] = sourceGroups.map((group) => ({
      name: group.label,
      rows: group.scopes.map((scope) => ({
        label: scope.label,
        value: scope.value,
        display: this.shortenScope(scope.value),
      })),
    }));

    const selectable = groups.flatMap((group) =>
      group.rows.map((row) => row.value)
    );
    const stored = this.normaliseValue(setting.value);

    // No saved selection means the entity is unconfigured: the gateway
    // requests the full scope set, so reflect that by checking everything.
    const selected = new Set(stored.length ? stored : selectable);

    return {
      ...definition,
      isMicrosoft: definition.driver === "microsoft",
      isGoogle: definition.driver === "google",
      expanded: false,
      saving: false,
      groups,
      locked: locked.map((value) => ({
        value,
        display: this.shortenScope(value),
      })),
      selected,
      initial: new Set(selected),
    };
  }

  private normaliseValue(value: string | string[]): string[] {
    return Array.isArray(value) ? value : [];
  }
}
